import { createTheme, Palette, PaletteColor, Shadows } from '@mui/material/styles'
// Type-only: teaches `theme.components` about MuiPickersDay and friends, which are MUI X's rather
// than MUI Material's.
import type {} from '@mui/x-date-pickers/themeAugmentation'
import shadows from './shadows'
import { blue, pink } from '@mui/material/colors'
import { LocationColor } from '../../shared-solo/lib/locationColorsHelper'

type PickByType<T, U> = {
  [K in keyof T]: T[K] extends U ? K : never
}[keyof T]

export type BaseColor = 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success'

export const onboardingGradientColours = [
  '#BBBBF7',
  '#FADBE5',
]

const locationColors: Record<LocationColor, string> = {
  MOBILE: '#D5D5FE',
  STATIC1: '#B3D7EE',
  STATIC2: '#C7E7CA',
  STATIC3: '#F7E2B8',
  STATIC4: '#F7CDF7',
  SIMPLE: '#D5D5FE', // (using MOBILE color as placeholder)
  SSPA: '#D5D5FE', // (using MOBILE color as placeholder)
}

export const iconColors = {
  defaultColor: '#46424A',
  redColor: '#DA4545',
  blackColor: '#000000',
  yellowColor: '#FCB415',
  lilacColor: '#9585B5',
  purpleColor: '#9A7B91',
  greenColor: '#70BB93',
  primaryColor: '#5C5AEA',
  secondaryColor: '#D3D2F7',
  grayColor: '#9F9FB6',
  lightGrayColor: '#C4BFCF',
  blueColor: '#93bedb',
  peachColor: '#F4E3C9',
  whiteColor: '#FFFFFF',
  eliteColor: '#35324a',
  orangeColor: '#F7C09A',
}

// Define custom colours on the pallete
declare module '@mui/material/styles' {
  interface Palette {
    blue: Palette['primary']
    darkGray: Palette['primary']
    location: typeof locationColors
  }

  interface PaletteOptions {
    blue?: PaletteOptions['primary'],
    darkGray?: PaletteOptions['primary'],
    location?: typeof locationColors
  }

  interface TypographyVariants {
    body0: React.CSSProperties
  }

  interface TypographyVariantsOptions {
    body0?: React.CSSProperties
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    body0: true
  }
}

export type MainMaterialColor = PickByType<Palette, PaletteColor>
export type MaterialColor = MainMaterialColor | `#${string}` | `${MainMaterialColor}.${keyof PaletteColor}`

// Update the Component color options to include custom colours
declare module '@mui/material/Chip' {
  interface ChipPropsColorOverrides {
    blue: true
    pink: true
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    darkGray: true
  }
}

const primaryTextColor = '#36333d'
export const DEFAULT_SPACING = 6

export const themeOptions = {
  palette: {
    mode: 'light',
    action: {
      hover: '#fafaff',
    },
    primary: {
      main: '#5C5AEA',
      contrastText: '#ffffff',
      light: '#6d6ce7',
      dark: '#5756ca',
    },
    secondary: {
      main: '#f8f7ff',
      contrastText: '#5C5AEA',
      light: '#fbfbff',
      dark: '#ededff',
    },
    error: {
      main: '#c34b3b',
      light: '#d25b4b',
      contrastText: '#ffffff',
    },
    warning: {
      main: '#e09947',
      light: '#f0b366',
      dark: '#c18137',
      contrastText: '#ffffff',
    },
    info: {
      main: '#6f6fb3',
      light: '#8f8fd3',
      contrastText: '#ffffff',
    },
    success: {
      main: '#54ad7a',
      light: '#5dc98c',
      transparent: '#5dc98c4f',
      contrastText: '#ffffff',
    },
    background: {
      default: '#f6f6fa',
      paper: '#ffffff',
    },
    divider: '#f1f1fa',
    text: {
      primary: primaryTextColor,
      secondary: '#817c8e',
    },
    darkGray: {
      main: primaryTextColor,
      contrastText: '#ffffff',
      light: '#eae9ff',
      dark: '#eae9ff',
    },
    blue: {
      main: blue[500],
      light: blue[100],
      dark: blue[700],
      contrastText: '#ffffff',
    },
    pink: {
      main: pink[300],
      light: pink[100],
      dark: pink[700],
      contrastText: '#ffffff',
    },
    location: locationColors,
  },
  shape: {
    borderRadius: 10,
  },
  spacing: DEFAULT_SPACING,
  typography: {
    button: {
      textTransform: 'none',
    },
    fontFamily: 'DM Sans',
    fontWeightMedium: 600,
    fontWeightBold: 800,
    fontSize: 13,
    h1: {
      fontWeight: 600,
      fontSize: '3.815rem',
    },
    h2: {
      fontWeight: 600,
      fontSize: '3.052rem',
    },
    h3: {
      fontWeight: 600,
      fontSize: '2.441rem',
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.953rem',
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.563rem',
    },
    h6: {
      fontWeight: 600,
      fontSize: '1.25rem',
    },
    body0: {
      fontSize: '1.1rem',
    },
    overline: {
      fontWeight: 600,
      fontSize: 12,
    },
  },
  colorSchemes: {
    dark: {
      palette: {
        mode: 'dark',
        action: {
          hover: '#2a2a3f',
        },
        primary: {
          main: '#7876FF', // Brightened for dark bg
          contrastText: '#ffffff',
          light: '#9190ff',
          dark: '#5C5AEA',
        },
        secondary: {
          main: '#2a2a3f', // Darkened version
          contrastText: '#7876FF',
          light: '#363652',
          dark: '#1f1f2e',
        },
        error: {
          main: '#ff6b5b', // Brightened for visibility
        },
        warning: {
          main: '#ffb366', // Brightened
          dark: '#e09947',
          contrastText: '#000000',
        },
        info: {
          main: '#8f8fd3', // Brightened
        },
        success: {
          main: '#6eda9a', // Brightened
        },
        background: {
          default: '#121218', // Dark background
          paper: '#1a1a23', // Slightly lighter than default
        },
        divider: '#2a2a3f',
        text: {
          primary: '#ffffff',
          secondary: '#a6a3b0', // Lightened for readability
        },
        darkGray: {
          main: '#b6b6d1',
          contrastText: '#ffffff',
          light: '#b6b6d1',
          dark: '#b6b6d1',
        },
        blue: {
          main: blue[400], // Using lighter shade of blue
          light: blue[300],
          dark: blue[600],
          contrastText: '#ffffff',
        },
      },
    },
    light: true,
  },
  shadows: shadows as Shadows,
  components: {
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          fontWeight: 'bold',
        },
        colorSuccess: {
          color: '#ffffff',
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          margin: 0,
          padding: 0,
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          margin: 0,
          padding: 0,
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          '&.MuiListItem-padding': {
            padding: `${DEFAULT_SPACING * 2}px ${DEFAULT_SPACING * 3}px`,
          },
        },
      },
    },
  },
} as const

const theme = createTheme(themeOptions)

theme.components!.MuiSwitch = {
  styleOverrides: {
    root: {
      padding: 6,
      '& .MuiSwitch-track': {
        borderRadius: 30,
        opacity: '0.2 !important',
        border: `1px solid ${theme.palette.secondary.dark}`,
        backgroundColor: theme.palette.primary.light,
      },
      '& .Mui-checked+.MuiSwitch-track': {
        backgroundColor: theme.palette.primary.main,
        opacity: '1 !important',
      },
      '& .Mui-checked.Mui-disabled+.MuiSwitch-track': {
        opacity: '0.4 !important',
      },
      '& .MuiSwitch-thumb': {
        backgroundColor: theme.palette.primary.contrastText,
        boxShadow: shadows[10],
        border: `1px solid ${theme.palette.secondary.light}`,
        opacity: 1,
      },
    },
  },
}

theme.components!.MuiPopover = {
  styleOverrides: {
    paper: {
      border: '1px solid', // divider
      borderColor: theme.palette.secondary.dark,
      display: 'flex',
      flexDirection: 'column',
      minWidth: 6 * 40,
    },
  },
}

theme.components!.MuiCardContent = {
  styleOverrides: {
    root: {
      padding: theme.spacing(2, 3),
    },
  },
}

theme.components!.MuiCardActions = {
  styleOverrides: {
    root: {
      padding: theme.spacing(1),
    },
  },
}

// Date pickers, sized for a pointer rather than a thumb on a phone. MUI's 36px day is a small
// target on a desktop, and every modal a picker opens in had room to spare — so the day grows and
// the calendar grows with it, keeping the rows on one screen without scrolling.
const dayCellSize = DEFAULT_SPACING * 8
// Each side, so a cell occupies dayCellSize + dayCellGap * 2.
const dayCellGap = 2
const dayRowHeight = dayCellSize + dayCellGap * 2

theme.components!.MuiDateCalendar = {
  styleOverrides: {
    root: {
      width: 7 * dayRowHeight + DEFAULT_SPACING * 4,
      // Six week rows, the weekday labels above them, and the month header — fixed, so the modal
      // doesn't resize as you page through months with five rows and six.
      height: 6 * dayRowHeight + dayCellSize + DEFAULT_SPACING * 8,
      maxHeight: 'none',
    },
  },
}

theme.components!.MuiDayCalendar = {
  styleOverrides: {
    weekDayLabel: {
      width: dayCellSize,
      height: dayCellSize,
      margin: `0 ${dayCellGap}px`,
      fontSize: '0.875rem',
      fontWeight: 'bold',
    },
    slideTransition: {
      minHeight: 6 * dayRowHeight,
    },
  },
}

theme.components!.MuiPickersDay = {
  styleOverrides: {
    root: {
      width: dayCellSize,
      height: dayCellSize,
      margin: `0 ${dayCellGap}px`,
      fontSize: '1rem',
    },
  },
}

theme.components!.MuiPickersCalendarHeader = {
  styleOverrides: {
    root: {
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(2),
    },
    label: {
      fontSize: '1.125rem',
      fontWeight: 'bold',
    },
  },
}

theme.components!.MuiBadge = {
  styleOverrides: {
    badge: {
      height: theme.spacing(3),
      minWidth: theme.spacing(3),
      padding: 0,
    },
  },
}

export default theme

export const spacingToNumber = (spacing: string) => {
  return Number(spacing.replace('px', ''))
}